import catalogManifest from "../data/catalogImages.json" with { type: "json" };
import { imageKey } from "./imageKey.ts";

const catalog: Record<string, (string | number)[]> = catalogManifest;
export function catalogImage(src: string | undefined) {
  // Normal Vite dev must also work from a clean checkout before derivatives
  // have been generated. Production/SSR and build tools use the manifest.
  if (import.meta.env?.DEV === true) return undefined;
  if (!src) return undefined;
  const item = catalog[imageKey(src)];
  return item ? { version: String(item[0]), width: Number(item[1]), height: Number(item[2]) } : undefined;
}
export function catalogImageSet(src: string | undefined, format: "avif" | "webp") {
  const image = catalogImage(src);
  if (!image) return undefined;
  return [...new Set([400, 640, 800, 1200, 1600].filter((w) => w < image.width).concat(image.width))]
    .map((width) => `/generated-media/remote-${image.version}-${width}.${format} ${width}w`).join(", ");
}

type ImageManifest = {
  versions: Record<string, string>;
  responsive?: Record<string, { webp: string; avif: string }>;
};
// Desktop galleries are 420px tall with object-cover. A 16:9 source needs
// ~750 source CSS pixels even when its visible box is narrower.
export const PRODUCT_IMAGE_SIZES = "(min-width: 1024px) 750px, calc(100vw - 80px)";
export const BLOG_IMAGE_SIZES = "(min-width: 1024px) 780px, (min-width: 780px) 732px, calc(100vw - 48px)";

function shopifyImageUrl(src: string): URL | undefined {
  try {
    const url = new URL(src, "https://www.reframeyouth.com");
    return url.hostname === "cdn.shopify.com" || url.hostname.endsWith(".myshopify.com")
      || (/(^|\.)reframeyouth\.com$/.test(url.hostname) && url.pathname.startsWith("/cdn/shop/")) ? url : undefined;
  } catch { return undefined; }
}

export function localImageSet(input: string | undefined, format: "avif" | "webp", manifest: ImageManifest): string | undefined {
  if (!input) return undefined;
  const candidates = new Map<string, string>();
  for (const item of input.split(",")) {
    const [url, declaredWidth] = item.trim().split(/\s+/);
    const recipe = manifest.responsive?.[url]?.[format];
    if (!recipe) return undefined;
    for (const candidate of recipe.split(",")) {
      const [src, width] = candidate.trim().split(/\s+/);
      // Preserve desktop cover-image resolution/crop quality. Small-phone
      // sources retain a >=640px floor, including tall object-cover heroes.
      if (parseInt(width) < (parseInt(declaredWidth) || 640) && recipe.split(",").length > 1) continue;
      if (!candidates.has(width)) {
        const version = manifest.versions[src];
        candidates.set(width, `${src}${version ? `?v=${version}` : ""} ${width}`);
      }
    }
  }
  return [...candidates.values()].join(", ");
}

export function shopifyImage(src: string | undefined, width: number): string | undefined {
  if (!src) return src;
    const local = catalogImageSet(src, "webp")?.split(", ").map((candidate) => candidate.split(" "));
    if (local) return (local.find(([, w]) => parseInt(w) >= width) ?? local.at(-1))![0];
    const url = shopifyImageUrl(src);
    if (!url) return src;
    url.searchParams.set("width", String(width));
    url.searchParams.set("format", "webp");
    return url.href;
}
export function shopifyImageSet(src: string | undefined): string | undefined {
  const local = catalogImageSet(src, "webp");
  if (local) return local;
  if (!src || !shopifyImageUrl(src)) return undefined;
  return [160, 320, 480, 640, 800, 860, 1024, 1280, 1600].map((width) => `${shopifyImage(src, width)} ${width}w`).join(", ");
}

/** Content images are below the dedicated, eagerly loaded article hero. */
export function optimizeArticleImages(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  for (const image of doc.querySelectorAll("img")) {
    image.loading = "lazy";
    image.decoding = "async";
    const set = shopifyImageSet(image.getAttribute("src") ?? undefined);
    if (set) {
      image.src = shopifyImage(image.src, 1280)!;
      image.srcset = set;
      image.sizes = BLOG_IMAGE_SIZES;
    }
  }
  return doc.body.innerHTML;
}
